import { DestroyRef, inject, Injectable, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import { ProductReference } from '../models/layout/product-reference';
import { ProductStatus } from '../models/layout/product-status';
import { GroupedProductReference } from '../models/grouped-product-reference';
import { LayoutService } from './layout.service';

export interface FilterOption {
  name: string;
  count: number;
}

export type FilterCategory =
  | 'rangeStatus'
  | 'refreshPlan'
  | 'lifecycle'
  | 'brand'
  | 'manufacturer'
  | 'category'
  | 'segment'
  | 'family'
  | 'base';

@Injectable({
  providedIn: 'root'
})
export class ProductBinService implements OnInit {
  private productsSubject = new BehaviorSubject<ProductReference[]>([]);
  private searchTermSubject = new BehaviorSubject<string>('');
  private showOnLayoutSubject = new BehaviorSubject<boolean>(true);
  private showOffLayoutSubject = new BehaviorSubject<boolean>(true);

  private selectedRangeStatusSubject = new BehaviorSubject<Set<string>>(new Set());
  private selectedRefreshPlanSubject = new BehaviorSubject<Set<string>>(new Set());
  private selectedLifecycleStatusSubject = new BehaviorSubject<Set<string>>(new Set());
  private selectedBrandsSubject = new BehaviorSubject<Set<string>>(new Set());
  private selectedManufacturersSubject = new BehaviorSubject<Set<string>>(new Set());
  private selectedCategoriesSubject = new BehaviorSubject<Set<string>>(new Set());
  private selectedSegmentsSubject = new BehaviorSubject<Set<string>>(new Set());
  private selectedFamiliesSubject = new BehaviorSubject<Set<string>>(new Set());
  private selectedBasesSubject = new BehaviorSubject<Set<string>>(new Set());

  public selectedRangeStatus$ = this.selectedRangeStatusSubject.asObservable();
  public selectedRefreshPlan$ = this.selectedRefreshPlanSubject.asObservable();
  public selectedLifecycleStatus$ = this.selectedLifecycleStatusSubject.asObservable();
  public selectedBrands$ = this.selectedBrandsSubject.asObservable();
  public selectedManufacturers$ = this.selectedManufacturersSubject.asObservable();
  public selectedCategories$ = this.selectedCategoriesSubject.asObservable();
  public selectedSegments$ = this.selectedSegmentsSubject.asObservable();
  public selectedFamilies$ = this.selectedFamiliesSubject.asObservable();
  public selectedBases$ = this.selectedBasesSubject.asObservable();

  public availableBrands$: Observable<FilterOption[]>;
  public availableManufacturers$: Observable<FilterOption[]>;
  public availableCategories$: Observable<FilterOption[]>;
  public availableSegments$: Observable<FilterOption[]>;
  public availableFamilies$: Observable<FilterOption[]>;
  public availableBases$: Observable<FilterOption[]>;

  // Alternate-configuration tree state. Lives in the service so deeply nested
  // ProductBinProductComponent instances can read/toggle expansion without
  // prop-drilling, and so it survives re-renders.
  private allProductsMap = new Map<number, ProductReference>();
  private childrenByParentId = new Map<number, ProductReference[]>();
  private expandedContainersSubject = new BehaviorSubject<Set<number>>(new Set());
  public expandedContainers$ = this.expandedContainersSubject.asObservable();

  private destroyRef = inject(DestroyRef);

  constructor(private layoutService: LayoutService) {
    // The product bin is derived from planogramItems, which only changes on
    // import/refresh/remove. Subscribing to layout$ rebuilt the bin's product
    // list + parent/child maps on every drag pickup, drop, and undo/redo.
    this.layoutService.productsChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updateProducts();
    });

    this.availableBrands$ = this.productsSubject.pipe(
      map(p => this.computeOptions(p, x => x.brand))
    );
    this.availableManufacturers$ = this.productsSubject.pipe(
      map(p => this.computeOptions(p, x => x.manufacturer))
    );
    this.availableCategories$ = this.productsSubject.pipe(
      map(p => this.computeOptions(p, x => x.categoryLevel1))
    );
    this.availableSegments$ = this.productsSubject.pipe(
      map(p => this.computeOptions(p, x => x.categoryLevel2))
    );
    this.availableFamilies$ = this.productsSubject.pipe(
      map(p => this.computeOptions(p, x => x.categoryLevel3))
    );
    this.availableBases$ = this.productsSubject.pipe(
      map(p => this.computeOptions(p, x => x.categoryLevel4))
    );
  }

  ngOnInit(): void {
    this.updateProducts();
  }

  setShowOnLayout(value: boolean) {
    this.showOnLayoutSubject.next(value);
  }

  setShowOffLayout(value: boolean) {
    this.showOffLayoutSubject.next(value);
  }

  setSearchTerm(term: string) {
    this.searchTermSubject.next(term.toLowerCase());
  }

  toggleFilter(category: FilterCategory, value: string) {
    const subject = this.getSubjectForCategory(category);
    if (!subject) return;
    const next = new Set(subject.value);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    subject.next(next);
  }

  isFilterSelected(category: FilterCategory, value: string): boolean {
    return this.getSubjectForCategory(category)?.value.has(value) ?? false;
  }

  clearAllFilters() {
    this.selectedRangeStatusSubject.next(new Set());
    this.selectedRefreshPlanSubject.next(new Set());
    this.selectedLifecycleStatusSubject.next(new Set());
    this.selectedBrandsSubject.next(new Set());
    this.selectedManufacturersSubject.next(new Set());
    this.selectedCategoriesSubject.next(new Set());
    this.selectedSegmentsSubject.next(new Set());
    this.selectedFamiliesSubject.next(new Set());
    this.selectedBasesSubject.next(new Set());
  }

  getActiveFilterCount(): number {
    return (
      this.selectedRangeStatusSubject.value.size +
      this.selectedRefreshPlanSubject.value.size +
      this.selectedLifecycleStatusSubject.value.size +
      this.selectedBrandsSubject.value.size +
      this.selectedManufacturersSubject.value.size +
      this.selectedCategoriesSubject.value.size +
      this.selectedSegmentsSubject.value.size +
      this.selectedFamiliesSubject.value.size +
      this.selectedBasesSubject.value.size
    );
  }

  getCurrentlyFilteredProductIds(): number[] {
    const products = this.productsSubject.value;
    const searchTerm = this.searchTermSubject.value;
    const filtered = this.applyAllFilters(products, searchTerm);
    return filtered.map(p => p.productId);
  }

  getFilteredProducts(): Observable<ProductReference[]> {
    return this.buildFilterStream().pipe(
      map(({ products, searchTerm, showOnLayout, showOffLayout, layoutBreakdown }) => {
        let filtered = this.applyAllFilters(products, searchTerm);
        filtered = this.applyLayoutVisibility(filtered, showOnLayout, showOffLayout, layoutBreakdown);
        return filtered;
      })
    );
  }

  getFilteredProductsByGroup(group: string): Observable<GroupedProductReference[]> {
    return this.buildFilterStream().pipe(
      map(({ products, searchTerm, showOnLayout, showOffLayout, layoutBreakdown }) => {
        let filtered = this.applyAllFilters(products, searchTerm);
        filtered = this.applyLayoutVisibility(filtered, showOnLayout, showOffLayout, layoutBreakdown);

        const grouped = filtered.reduce((acc: GroupedProductReference[], product) => {
          let groupKey: string;

          if (group === 'Family') {
            groupKey = product.categoryLevel3;
          } else if (group === 'Brand') {
            groupKey = product.brand;
          } else if (group === 'Category') {
            groupKey = product.categoryLevel1;
          } else if (group === 'Segment') {
            groupKey = product.categoryLevel2;
          } else if (group === 'Base') {
            groupKey = product.categoryLevel4;
          } else if (group === 'Flow') {
            groupKey = product.categoryFlow.toString();
          } else {
            groupKey = product[group as keyof ProductReference] as string;
          }

          const existingGroup = acc.find(g => g.group === groupKey);
          if (existingGroup) {
            existingGroup.products.push(product);
          } else {
            acc.push({ group: groupKey, products: [product] });
          }
          return acc;
        }, []);

        if (group === 'Flow') {
          return grouped.sort((a, b) => Number(a.group) - Number(b.group));
        }
        return grouped.sort((a, b) => a.group.localeCompare(b.group));
      })
    );
  }

  private buildFilterStream() {
    return combineLatest([
      this.productsSubject,
      this.searchTermSubject,
      this.showOnLayoutSubject,
      this.showOffLayoutSubject,
      this.layoutService.layoutBreakdown$,
      this.selectedRangeStatusSubject,
      this.selectedRefreshPlanSubject,
      this.selectedLifecycleStatusSubject,
      this.selectedBrandsSubject,
      this.selectedManufacturersSubject,
      this.selectedCategoriesSubject,
      this.selectedSegmentsSubject,
      this.selectedFamiliesSubject,
      this.selectedBasesSubject
    ]).pipe(
      map(([products, searchTerm, showOnLayout, showOffLayout, layoutBreakdown]) => ({
        products,
        searchTerm,
        showOnLayout,
        showOffLayout,
        layoutBreakdown
      }))
    );
  }

  private applyLayoutVisibility(
    products: ProductReference[],
    showOnLayout: boolean,
    showOffLayout: boolean,
    layoutBreakdown: Map<number, number>
  ): ProductReference[] {
    return products.filter(product => {
      const isOnLayout =
        layoutBreakdown.has(product.productId) &&
        layoutBreakdown.get(product.productId)! > 0;
      return (isOnLayout && showOnLayout) || (!isOnLayout && showOffLayout);
    });
  }

  private applyAllFilters(products: ProductReference[], searchTerm: string): ProductReference[] {
    // Apply filters to ALL products first to match children too
    let matchingCandidates = products;

    if (searchTerm) {
      matchingCandidates = matchingCandidates.filter(product =>
        product.description.toLowerCase().includes(searchTerm) ||
        product.barcode.toLowerCase().includes(searchTerm) ||
        product.brand.toLowerCase().includes(searchTerm)
      );
    }

    const rangeStatus = this.selectedRangeStatusSubject.value;
    if (rangeStatus.size > 0) {
      matchingCandidates = matchingCandidates.filter(p =>
        (rangeStatus.has('Ranged') && p.ranged === true) ||
        (rangeStatus.has('Not Ranged') && p.ranged !== true)
      );
    }

    const refreshPlan = this.selectedRefreshPlanSubject.value;
    if (refreshPlan.size > 0) {
      matchingCandidates = matchingCandidates.filter(p =>
        (refreshPlan.has('Will Add') && p.willAdd === true) ||
        (refreshPlan.has('Will Remove') && p.willRemove === true)
      );
    }

    const lifecycle = this.selectedLifecycleStatusSubject.value;
    if (lifecycle.size > 0) {
      matchingCandidates = matchingCandidates.filter(p =>
        (lifecycle.has('NPI') && p.statusId === ProductStatus.NPI) ||
        (lifecycle.has('Active') && p.statusId === ProductStatus.Active) ||
        (lifecycle.has('Discontinued') && p.statusId === ProductStatus.Discontinued) ||
        (lifecycle.has('TB Discontinued') && p.statusId === ProductStatus.ToBeDiscontinued)
      );
    }

    matchingCandidates = this.applyDynamic(matchingCandidates, this.selectedBrandsSubject.value, p => p.brand);
    matchingCandidates = this.applyDynamic(matchingCandidates, this.selectedManufacturersSubject.value, p => p.manufacturer);
    matchingCandidates = this.applyDynamic(matchingCandidates, this.selectedCategoriesSubject.value, p => p.categoryLevel1);
    matchingCandidates = this.applyDynamic(matchingCandidates, this.selectedSegmentsSubject.value, p => p.categoryLevel2);
    matchingCandidates = this.applyDynamic(matchingCandidates, this.selectedFamiliesSubject.value, p => p.categoryLevel3);
    matchingCandidates = this.applyDynamic(matchingCandidates, this.selectedBasesSubject.value, p => p.categoryLevel4);

    // Track matching product's root parents (only roots should be on the top level)
    const rootIds = new Set<number>();
    const rootProducts: ProductReference[] = [];

    for (const candidate of matchingCandidates) {
      let root = candidate;
      const visited = new Set<number>();
      
      while (root.parentProductId != null && !visited.has(root.productId)) {
        visited.add(root.productId);
        const parent = this.allProductsMap.get(root.parentProductId);
        if (!parent) {
          break;
        }
        root = parent;
      }

      if (!rootIds.has(root.productId)) {
        rootIds.add(root.productId);
        rootProducts.push(root);
      }
    }

    return rootProducts;
  }

  private applyDynamic(
    products: ProductReference[],
    selected: Set<string>,
    accessor: (p: ProductReference) => string | null | undefined
  ): ProductReference[] {
    if (selected.size === 0) return products;
    return products.filter(p => {
      const v = accessor(p);
      return v != null && selected.has(v);
    });
  }

  private computeOptions(
    products: ProductReference[],
    accessor: (p: ProductReference) => string | null | undefined
  ): FilterOption[] {
    const counts = new Map<string, number>();
    products.forEach(p => {
      const key = accessor(p);
      if (key) {
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    });
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private getSubjectForCategory(category: FilterCategory): BehaviorSubject<Set<string>> | null {
    switch (category) {
      case 'rangeStatus': return this.selectedRangeStatusSubject;
      case 'refreshPlan': return this.selectedRefreshPlanSubject;
      case 'lifecycle': return this.selectedLifecycleStatusSubject;
      case 'brand': return this.selectedBrandsSubject;
      case 'manufacturer': return this.selectedManufacturersSubject;
      case 'category': return this.selectedCategoriesSubject;
      case 'segment': return this.selectedSegmentsSubject;
      case 'family': return this.selectedFamiliesSubject;
      case 'base': return this.selectedBasesSubject;
      default: return null;
    }
  }

  private updateProducts() {
    const products = Array.from(this.layoutService.planogramItems.values());

    this.allProductsMap = new Map(products.map(p => [p.productId, p]));
    this.childrenByParentId = new Map();
    for (const p of products) {
      if (p.parentProductId == null) continue;
      const siblings = this.childrenByParentId.get(p.parentProductId);
      if (siblings) siblings.push(p);
      else this.childrenByParentId.set(p.parentProductId, [p]);
    }

    this.productsSubject.next(products);
  }

  getDirectContainers(productId: number): ProductReference[] {
    return this.childrenByParentId.get(productId) ?? [];
  }

  hasContainers(productId: number): boolean {
    return this.childrenByParentId.has(productId);
  }

  getTotalBaseUnits(product: ProductReference): number {
    if (product.capacity == null) return 1;

    let total = product.capacity;
    let current: ProductReference | undefined = product;
    while (current && current.parentProductId != null) {
      const parent = this.allProductsMap.get(current.parentProductId);
      if (!parent || parent.capacity == null) break;
      total *= parent.capacity;
      current = parent;
    }
    return total;
  }

  toggleContainerExpansion(productId: number): void {
    const next = new Set(this.expandedContainersSubject.value);
    if (next.has(productId)) next.delete(productId);
    else next.add(productId);
    this.expandedContainersSubject.next(next);
  }

  isContainerExpanded(productId: number): boolean {
    return this.expandedContainersSubject.value.has(productId);
  }
}
